import { useState, useEffect, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Sparkles } from "lucide-react";

const FACTS = [
  "Did you know? Universities can detect personal statements written by AI. We never write or rewrite your statement — we tell you what admissions tutors will think, so you can improve it yourself, in your own words.",
  "Did you know? International applications to UK universities are at a record high. Top grades are no longer enough — admissions tutors look to your statement to tell you apart. We show you whether yours is doing that job.",
  "Did you know? Some UK courses admit fewer than 1 in 10 applicants. When that many strong candidates compete, the statement is often the tie-breaker. We give you tailored feedback so yours stands up.",
  "Our team is made up of graduates and tutors from leading UK universities, including Cambridge, UCL, Exeter, Durham and Liverpool, among others.",
  "We provide ethical feedback. We don't write your statement for you — we empower you to make the best possible impression.",
];

const ROTATE_MS = 7000;

export const FactBanner = () => {
  const [i, setI] = useState(0);
  const [dir, setDir] = useState(1);

  const go = useCallback((d) => {
    setDir(d);
    setI((p) => (p + d + FACTS.length) % FACTS.length);
  }, []);

  useEffect(() => {
    const t = setInterval(() => go(1), ROTATE_MS);
    return () => clearInterval(t);
  }, [go, i]);

  return (
    <section
      data-testid="fact-banner"
      className="relative z-[70] border-y border-border/70 bg-sand-deep/40"
    >
      <div className="max-w-4xl mx-auto px-6 sm:px-8 py-10">
        <div className="flex items-center gap-2 text-clay justify-center">
          <Sparkles className="h-4 w-4" strokeWidth={2} />
          <span className="text-xs font-semibold uppercase tracking-[0.2em]">
            Worth knowing
          </span>
        </div>

        <div className="mt-5 flex items-center gap-3 sm:gap-5">
          <button
            type="button"
            data-testid="fact-prev"
            aria-label="Previous fact"
            onClick={() => go(-1)}
            className="shrink-0 grid h-9 w-9 place-items-center rounded-full border border-border bg-white text-ink-muted hover:text-ink hover:border-clay transition-colors"
          >
            <ChevronLeft className="h-4 w-4" strokeWidth={2} />
          </button>

          <div className="flex-1 min-h-[88px] sm:min-h-[72px] grid place-items-center overflow-hidden">
            <AnimatePresence mode="wait" custom={dir}>
              <motion.p
                key={i}
                custom={dir}
                data-testid="fact-text"
                initial={{ opacity: 0, x: dir * 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: dir * -24 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                className="text-center text-base sm:text-lg leading-relaxed text-ink-soft"
              >
                {FACTS[i]}
              </motion.p>
            </AnimatePresence>
          </div>

          <button
            type="button"
            data-testid="fact-next"
            aria-label="Next fact"
            onClick={() => go(1)}
            className="shrink-0 grid h-9 w-9 place-items-center rounded-full border border-border bg-white text-ink-muted hover:text-ink hover:border-clay transition-colors"
          >
            <ChevronRight className="h-4 w-4" strokeWidth={2} />
          </button>
        </div>

        <div className="mt-5 flex items-center justify-center gap-2" data-testid="fact-dots">
          {FACTS.map((fact, idx) => (
            <button
              key={fact.slice(0, 28)}
              type="button"
              aria-label={`Go to fact ${idx + 1}`}
              onClick={() => {
                setDir(idx > i ? 1 : -1);
                setI(idx);
              }}
              className={`h-1.5 rounded-full transition-all ${
                idx === i ? "w-6 bg-clay" : "w-1.5 bg-border"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
